import { Component, Inject, Input, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { LocalStorageService } from 'ngx-webstorage';
import { Subscription } from 'rxjs/internal/Subscription';
import { BroadCastEvents } from '../../../common/broad-cast-events.enum';
import { SystemPreferences, SYSTEM_PREFERENCES } from '../../../common/system-preferences';
import { HistoryHelper } from '../../../helpers/history-helper';
import { getPermissionInfo } from '../../../helpers/permission.helpers';
import { NoteStatusEnum, NoteTypeEnum, OrderStatusEnum, PatientOrderStatusEnum } from '../../../models/enums';
import { NoteFormRequest } from '../../../models/notes';
import { PatientInfoWidgetRequest, PatientInfoWidgetViewModel, PatientStatusEnum } from '../../../models/patients';
import { SpecialActionAccesses} from '../../../models/roles';
import { SecureBaseComponentContext } from '../../../modules/tcm-common/services/secure-base-component-context.service';
import { FilesDataService, PatientDataService, PermissionsService, UserClinicService } from '../../../services';
import { SmsTemplateDataService } from '../../../services/sms-template-data.service';
import { TranslationService } from '../../../services/translation.service';
import { PatientAlertShowUpComponent } from './patient-alert-show-up/patient-alert-show-up.component';
import { PatientJournalComponent } from './patient-journal/patient-journal.component';
import { SendSmsComponent } from './send-sms/send-sms.component';
import { UpdatePhotoComponent } from './update-photo/update-photo.component';
import { TcmSecureBaseComponent } from '../../tcm-common/security/tcm-secure-base-component';
import { SendEmailComponent } from './send-email/send-email.component';
import { EmailTemplateDataService } from '../../../services/email-template-data.service';
import { PatientEquipmentWidgetLinkedSearchComponent } from './latest-equipment/patient-equipment-widget-search.component';
import { NullObjectSpecialActions } from '../../../modules/tcm-common/security/null-object-special-actions';

@Component({
    selector: 'patient-info-widget',
    templateUrl: './patient-info-widget.component.html',
    styleUrls: ['./patient-info-widget.component.scss'],
    standalone: false
})
export class PatientInfoWidgetComponent extends TcmSecureBaseComponent<PatientInfoWidgetRequest, PatientInfoWidgetViewModel> {

  @ViewChild("sendSmsTemplate", { static: true }) sendSmsTemplate: TemplateRef<any>;

  @Input() salesOrderNumber?: number;
  @Input() salesOrderStatus?: any;
  @Input() salesOrderId?: number;
  @Input() invoiceNumber?: number;

  @Input() salesOrderPatientBalance?: number;
  @Input() salesOrderTotalPatientPart?: number;
  @Input() isFinanced?: boolean;

  patientStatusTranslations: any;
  patientOrderStatusTranslations: any;
  expanded = false;
  clinicId: number;
  canReadSalesOrder: boolean;
  public serializedItemStatusTranslations: any;


  patientStatusEnum = PatientStatusEnum;
  orderStatusTranslations: any;
  orderStatusEnum = OrderStatusEnum;

  historyHelper = HistoryHelper.instance;

  private updateSubscription: Subscription;

  get newNoteRequestModel() {
    const model: NoteFormRequest = {
      type: NoteTypeEnum.Patient,
      typeKeyId: this.requestModel.patientId,
      status: NoteStatusEnum.New,
      patientId: this.requestModel.patientId
    };

    return model;
  }

  canEditNotes = false;

  readonly permissions = {
    documents: getPermissionInfo('patients', 'patientDocuments'),
  };

  get patientInactive(): boolean {
    return this.viewModel.status == PatientStatusEnum.Inactive || this.viewModel.status == PatientStatusEnum.Deceased;
  }
  get specialActionPermissions(): SpecialActionAccesses {
    return this.permissionService.currentPermissions
      ? this.permissionService.currentPermissions.specialActions
      : NullObjectSpecialActions.specialActions;
  }

  get leftSideWarrantyExpirationInactive():boolean{
    if(this.viewModel.leftEquipmentData && this.viewModel.leftEquipmentData.warrantyExpirationDate){
      let warrantyExpirationDateStr = this.viewModel.leftEquipmentData.warrantyExpirationDate;
      let warrantyExpirationDate = new Date(warrantyExpirationDateStr);
      return warrantyExpirationDate < new Date()
    }
    return true;
  }
  get rightSideWarrantyExpirationInactive():boolean{
    if(this.viewModel.rightEquipmentData && this.viewModel.rightEquipmentData.warrantyExpirationDate){
      let warrantyExpirationDateStr = this.viewModel.rightEquipmentData.warrantyExpirationDate;
      let warrantyExpirationDate = new Date(warrantyExpirationDateStr);
      return warrantyExpirationDate < new Date()
    }
    return false;
  }
  constructor(protected override componentContext: SecureBaseComponentContext,
    protected override route: ActivatedRoute,
    private permissionsService: PermissionsService,
    private translationService: TranslationService,
    private localStorageService: LocalStorageService,
    private userClinicService: UserClinicService,
    private patientDataService: PatientDataService,
    private smsTemplateDataService: SmsTemplateDataService,
    private emailTemplateDataService: EmailTemplateDataService,
    private permissionService: PermissionsService,
    private filesDataService: FilesDataService,
    @Inject(SYSTEM_PREFERENCES) private systemPref: SystemPreferences) {

    super(componentContext, route);

    this.clinicId = this.userClinicService.clinicId;
    this.actionName = 'PatientWidget/InfoWidget';
    this.serializedItemStatusTranslations = this.translationService.getSerializedItemStatusTranslations();

    this.loadUserSettings();

    this.patientStatusTranslations = this.translationService.getPatientStatusTranslations();
    this.patientOrderStatusTranslations = this.translationService.getPatientOrderStatusTranslations();
    this.orderStatusTranslations = this.translationService.getOrderStatusTranslations();

    this.updateSubscription = this.componentContext.broadcastService
      .getEvents(BroadCastEvents.PatientPhotoChanged,
        BroadCastEvents.PatientChanged,
        BroadCastEvents.InsuranceChanged,
        BroadCastEvents.AppointmentChanged,
        BroadCastEvents.RelatedCompanionChanged,
        BroadCastEvents.SalesOrderLineChanged,
        BroadCastEvents.SalesOrderLinesStatusChanged,
        BroadCastEvents.PatientAlertChanged,
        BroadCastEvents.PatientEquipmentChanged,
        BroadCastEvents.PatientServicePlanChanged)
      .subscribe(e => {
        if (e.name == BroadCastEvents.PatientPhotoChanged) {
          this.viewModel.photo = e.args.src;
        }
        else {
          this.requestModel.showAlerts = e.name != BroadCastEvents.PatientAlertChanged;

          this.refresh();
        }
      });

    this.permissionsInfo = getPermissionInfo('patients', 'patients');
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.updateSubscription.unsubscribe();
  }

  toggleExpand() {
    this.expanded = !this.expanded;
    this.saveUserSettings();
  }

  openMakePhotoModal($event) {
    $event.preventDefault();
    $event.stopPropagation();

    this.componentContext.modalService.openComponentInModal(UpdatePhotoComponent, "sm", { requestModel: { patientId: this.requestModel.patientId } });
  }

  openPatientJournal($event) {
    $event.preventDefault();
    $event.stopPropagation();

    this.componentContext.modalService.openComponentInModal(PatientJournalComponent, "sm", { requestModel: { patientId: this.requestModel.patientId } });
  }

  async createSmsMessageAsync() {
    if (!this.canEdit) {
      return;
    }

    this.isLoading = true;

    try {
      var smsTemplates = await this.smsTemplateDataService.getSmsTemplateOptionsAllAsync(this.clinicId, this.requestModel.patientId, this.componentCancellationTokenSource.promise());
    }
    finally {
      this.isLoading = false;
    }

    const sendSmsInfo = await this.componentContext.modalService.openComponentInModal(SendSmsComponent, 'sm', {
      clinicId: this.clinicId,
      patientId: this.requestModel.patientId,
      smsTemplates: smsTemplates
    });

    if (!sendSmsInfo) {
      return;
    }

    if (!sendSmsInfo.smsText || sendSmsInfo.smsText.length > 500) {
      await this.componentContext.modalService.showErrorModalAsync(x => x.Messages.MessageHeadersAndActions.Error, x => x.Screens.Patients.InfoWidget.Validation.SmsTextError);

      this.createSmsMessageAsync();
      return;
    }

    this.isLoading = true;
    var isSent = false;

    try {
      isSent = await this.patientDataService.sendSmsAsync(this.requestModel.patientId, this.viewModel.cellPhone, sendSmsInfo.smsText, this.userClinicService.clinicId, this.componentCancellationTokenSource.promise());
    }
    finally {
      this.isLoading = false;
    }

    if (!isSent) {
      await this.componentContext.modalService.showErrorModalAsync(x => x.Messages.MessageHeadersAndActions.Error, x => x.Messages.ErrorMessages.SmsWasNotSent);
    } else {
      this.componentContext.broadcastService.next({ name: BroadCastEvents.NoteChanged });
      this.componentContext.broadcastService.next({ name: BroadCastEvents.WorkQueueTouched });
      await this.componentContext.modalService.showSuccessModalAsync(x => x.Messages.MessageHeadersAndActions.Success, x => x.Screens.Patients.InfoWidget.Messages.SmsSendSuccess);
    }
  }

  async createEmailMessageAsync(email: string) {
    if (!this.canEdit) {
      return;
    }

    this.isLoading = true;

    try {
      var emailTemplates = await this.emailTemplateDataService.getEmailTemplateOptionsAllAsync(this.componentCancellationTokenSource.promise());
    }
    finally {
      this.isLoading = false;
    }

    await this.componentContext.modalService.openComponentInModal(SendEmailComponent, 'lg', {
      email: email,
      patientId: this.requestModel.patientId,
      clinicId: this.clinicId,
      emailTemplates: emailTemplates
    });
  }

  protected createRequestModel(): PatientInfoWidgetRequest {
    return {
      showAlerts: true,
      clinicId: this.clinicId,
    } as PatientInfoWidgetRequest;
  }

  protected override onViewModelChanged() {
    super.onViewModelChanged();

    if (this.viewModel.showUpAlerts && this.viewModel.showUpAlerts.length) {
      this.componentContext.modalService.openComponentInModal(PatientAlertShowUpComponent, "md", { alerts: this.viewModel.showUpAlerts });
    }
  }

  protected override onPermissionChanged() {
    this.canEditNotes = this.canEditNode('patients', 'patientNotes');
    this.canReadSalesOrder = this.canReadNode('sales', 'salesOrders');
  }

  private loadUserSettings() {
    const settings = this.localStorageService.retrieve("PatientInfoWidget");

    if (settings) {
      this.expanded = settings.isExpanded;
    }
  }

  private saveUserSettings() {
    const settings: PatientInfoWidgetUserSettings = {
      isExpanded: this.expanded
    };

    this.localStorageService.store("PatientInfoWidget", settings);
  }

  openEquipment($event) {
    this.componentContext.modalService.openComponentInModal(PatientEquipmentWidgetLinkedSearchComponent, "sm", { requestModel: { patientId: this.requestModel.patientId, productGroupId: null, status:null} });
  }

  onAttachmentClick(fileId: number) {
    this.filesDataService.downloadAndDisplayFileAsync(fileId);
  }
}

interface PatientInfoWidgetUserSettings {
  isExpanded: boolean;
}
